'use client'

import React, { useRef, useEffect } from "react";
import RoundedIcon from "@/components/ui/RoundedIcon/RoundedIcon";
import { Check } from "@phosphor-icons/react";
import Headline from "@/components/ui/Headline/Headline";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { useDetectWrap } from "@/hooks/useDetectWrap";
import LPForm from "@/components/LPForm/LPForm";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import Image from "next/image";

import { debug } from '@/lib/debug';
import { useIsLg, useIsMidxl2 } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface LPImageFormSectionProps {
  language?: LanguageType
  heading: string
  // subheading: string
  paragraph: string
  onInitialPosition?: (position: number) => void
  benefit1: string
  benefit2: string
  benefit3: string
  backgroundImage?: string
  shouldAnimate?: boolean
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const LPImageFormSection: React.FC<LPImageFormSectionProps> = ({
  language = "english",
  heading,
  // subheading,
  paragraph,
  onInitialPosition,
  benefit1,
  benefit2,
  benefit3,
  backgroundImage,
  shouldAnimate = true,
  id,
}) => {
  const isLg = useIsLg();
  const isMidxl2 = useIsMidxl2();
  const { ref } = useDetectWrap();
  const sectionRef = useRef<HTMLElement>(null);
  const backgroundContainerRef = useRef<HTMLDivElement>(null);
  const backgroundImageRef = useRef<HTMLImageElement>(null);
  const element1Ref = useRef<HTMLDivElement>(null);
  const element2Ref = useRef<HTMLDivElement>(null);
  const element3Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current && onInitialPosition) {
      const rect = sectionRef.current.getBoundingClientRect();
      const bottomY = window.scrollY + rect.bottom;
      onInitialPosition(bottomY);
    }
  }, [isLg, onInitialPosition]);

  useGSAP(() => {
    const element1 = element1Ref.current;
    const element2 = element2Ref.current;
    const element3 = element3Ref.current;

    if (!element1 || !element3) return;

    const elements = [element1, element3];
    if (element2) {
      elements.push(element2);
    }

    if (shouldAnimate) {
      // Set initial states for blur mask animation
      gsap.set(elements, {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });

      // Create scroll trigger animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: element1,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      // Animate elements with stagger
      tl.to(elements, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      });
    }

  }, { dependencies: [shouldAnimate], revertOnUpdate: true });

  // Parallax effect for background image
  useGSAP(() => {
    const image = backgroundImageRef.current;
    const container = backgroundContainerRef.current;

    if (!image || !container || !backgroundImage || !isLg) return;

    gsap.fromTo(image, {
      yPercent: -10
    }, {
      yPercent: 10,
      ease: "none",
      force3D: true,
      scrollTrigger: {
        trigger: container,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
        invalidateOnRefresh: true,
        id: "lp-image-form-section-parallax"
      },
      onEnter: () => {
        image.style.willChange = "transform";
      },
      onLeave: () => {
        image.style.willChange = "auto";
      },
      onEnterBack: () => {
        image.style.willChange = "transform";
      },
      onLeaveBack: () => {
        image.style.willChange = "auto";
      }
    });
  }, { dependencies: [isLg, backgroundImage], revertOnUpdate: true });

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      id={id}
      style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
      className="
      w-full
      p-[0.5rem]
      lg:p-[1.5rem]
      flex items-center justify-center
      h-fit
    ">
      <div
        ref={backgroundContainerRef}
        className="relative w-full border-radius-main border-main border-stroke-soft-200 p-[0.75rem] pt-[3.5rem] midxl2:p-[2rem] overflow-hidden"
      >
        {backgroundImage && (
          <Image
            ref={backgroundImageRef}
            src={backgroundImage}
            alt=""
            fill
            sizes="100vw"
            loading="lazy"
            className="object-cover -z-10"
            style={{ transformOrigin: 'center center', scale: 1.1 }}
          />
        )}
        <div className="relative flex flex-col items-center midxl2:flex-row midxl2:items-end lg:justify-between
          gap-y-[2rem] lg:gap-x-[0.25rem] lg:gap-y-[2.5rem] midxl2:gap-y-[4.5rem]
          ">
          <div className="
            w-full tb:w-[30.6875rem] tb:flex-grow tb:max-w-[49.6875rem]
            flex flex-col gap-[2rem] lg:gap-[3.5rem] justify-end
            midxl2:pb-[3.5rem] lg:px-[0.25rem] midxl2:px-[0.5rem] midxl:px-[1rem] 3xl:px-[3.5rem]  lg:pt-0
            ">

            <div className="flex flex-col gap-[1.5rem]">
              <div className="flex flex-row">
                <Headline
                  type="long"
                  heading={heading}
                  // paragraph={paragraph}
                  language={language}
                  alignment={isMidxl2 ? language === "arabic" ? "right" : "left" : "center"}
                  textColor="text-text-reversed"
                  shouldAnimate={false}
                />
              </div>
              <p
              style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
               className={`text-text-reversed text-18--alexandria w-full tb:max-w-[30.6875rem] text-center ${language === 'arabic' ? 'midxl2:text-right' : 'midxl2:text-left'}`}>
                {paragraph}
              </p>
            </div>

            {isMidxl2 && (
              <div className="w-full tb:max-w-[37rem] flex flex-col gap-[2rem]">
                <div ref={element1Ref} className="flex flex-col gap-[1.5rem]">
                  <div className="flex flex-col gap-[0.75rem] lg:flex-row lg:gap-[1rem] items-start lg:items-center justify-start">
                    <RoundedIcon
                      theme="dark"
                      className="rounded-full"
                      size={44}
                    >
                      <Check className="text-graphics-reversed" />
                    </RoundedIcon>
                    <div className="flex flex-col gap-[0.5rem]">
                      <p className="text-text-reversed text-28--neue-june">
                        {benefit1}
                      </p>
                    </div>
                  </div>
                </div>

                <div ref={element2Ref} className="flex flex-col gap-[1.5rem]">
                  <div className="flex flex-col gap-[0.75rem] lg:flex-row lg:gap-[1rem] items-start lg:items-center justify-start">
                    <RoundedIcon
                      theme="dark"
                      className="rounded-full"
                      size={44}
                    >
                      <Check className="text-graphics-reversed" />
                    </RoundedIcon>
                    <div className="flex flex-col gap-[0.5rem]">
                      <p className="text-text-reversed text-28--neue-june">
                        {benefit2}
                      </p>
                    </div>
                  </div>
                </div>

                <div ref={element3Ref} className="flex flex-col gap-[1.5rem]">
                  <div className="flex flex-col gap-[0.75rem] lg:flex-row lg:gap-[1rem] items-start lg:items-center justify-start">
                    <RoundedIcon
                      theme="dark"
                      className="rounded-full"
                      size={44}
                    >
                      <Check className="text-graphics-reversed" />
                    </RoundedIcon>
                    <div className="flex flex-col gap-[0.5rem]">
                      <p className="text-text-reversed text-28--neue-june">
                        {benefit3}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          <div
            ref={ref}
            className="w-fit"
          // style={{
          //   maxWidth: isLg && !isWrapped ? '43.3125rem' : '100%',
          //   paddingTop: isLg && !isWrapped ? '14.625rem' : 0
          // }}
          >
            <LPForm
              language={language}
              headlineText={language === 'arabic' ? "أبدئوا رحلتكم الجمالية الشخصية" : "Begin your personalized beauty journey"}
              subheadingText={language === 'arabic' ? "وصول محدود" : "LIMITED ACCESS"}
              benefits={[benefit1, benefit2, benefit3]}
              onSubmit={(formData) => {
                debug.log('Form submitted:', formData);
                // Handle form submission
              }}
              innerHorizontalPadding="px-0 tb:px-[3.5rem]"
              innerVerticalPadding="p-[1.5rem] tb:pt-[5rem] tb:pb-[3.5rem]"
            />
          </div>
        </div>
      </div>

    </section >
  );
}


export default LPImageFormSection;
